import { useContext } from 'react'

import {
  Button, FileInput, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignContribution, { useCampaignContributionForm } from '@hooks/useCampaignContribution'
import useActivities from '@hooks/useActivities'

import { paidByOptions, paymentMethods } from '@functions/campaignContribution'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import Activities from '@components/Activities'
import type { CampaignContributionModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditCampaignContributionModal'

type ModalContextTypes = {
  baseModalKey: string,
  modalData: {
    campaignContribution: Partial<CampaignContributionModel>,
  },
}

type ModalCallbacks = {
  closeAllModals: () => void,
  closeModal: () => void,
  createCampaignContribution: (payload: HandleCallbackActionParams) => void,
  updateCampaignContribution: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignContributionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeAllModals, closeModal, createCampaignContribution, updateCampaignContribution,
  } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData, baseModalKey } = modalContext

  const isOnlyModalOpen = baseModalKey === MODAL_KEY

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const campaignContribution = digObject(modalPayload, 'campaignContribution', {})

  const newRecord = !campaignContribution.id

  const {
    callbacks: {
      createCampaignContribution: createFn,
      updateCampaignContribution: updateFn,
    },
    creating,
    updating,
  } = useCampaignContribution(campaignContribution)

  const {
    entityState,
    entityState: {
      amount,
      date_received,
      paid_by,
      payment_method,
      reference,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCampaignContributionForm(campaignContribution, { validateOn: 'change' })

  const createOrUpdateCampaignContributionPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: isOnlyModalOpen ? closeModal : closeAllModals,
    },
    entityParams: entityState,
    requestOptions: {
      include_campaign_contribution_receipt: true,
    },
    toastText: `Campaign Contribution ${newRecord ? 'Created' : 'Updated'}`,
  }

  // Activities
  const activitiesPayload = useActivities({
    filters: { recipient_id: campaignContribution?.id, recipient_type: 'CampaignContribution' },
    performHttpRequests: !!campaignContribution?.id,
  })

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Contribution"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Date Received" errorMessage={errors.date_received}>
            <input name="date_received" type="date" value={date_received} {...handlers} />
          </Form.Field>

          <Form.Field label="* Reference/Receipt" errorMessage={errors.reference} marginTop="large">
            <input name="reference" type="text" value={reference} {...handlers} />
          </Form.Field>

          <Form.Field label="* Amount" errorMessage={errors.amount} marginTop="large">
            <input name="amount" type="number" value={amount} {...handlers} />
          </Form.Field>

          <Form.Field label="* Paid By" errorMessage={errors.paid_by} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="paid_by" value={paid_by} onChange={handlers.onChange}>
              <option value="">Please Select...</option>
              {paidByOptions.map(paidByOption => (
                <option key={paidByOption.key} value={paidByOption.key}>
                  {paidByOption.text}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="* Payment Method" errorMessage={errors.payment_method} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="payment_method" value={payment_method} onChange={handlers.onChange}>
              <option value="">Please Select...</option>
              {paymentMethods.map(paymentMethod => (
                <option key={paymentMethod.key} value={paymentMethod.key}>
                  {paymentMethod.text}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="Receipt" marginTop="large">
            <FileInput
              // @ts-expect-error TS2322
              name="receipt"
              input={{
                onChange: (fileData: object, fileObject: object) => {
                  // @ts-expect-error TS2339
                  setEntityState({ receipt_data: fileData, receipt_file_name: fileObject.name })
                },
              }}
            />
          </Form.Field>
        </Form>

        {!newRecord && (
          <Activities activitiesPayload={activitiesPayload} />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignContribution(createOrUpdateCampaignContributionPayload)
              : () => updateCampaignContribution(createOrUpdateCampaignContributionPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Campaign Contribution` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignContributionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
